/**
 * Página de configuración runtime por módulo (Fase 32).
 *
 * Cada slug registra su componente custom en `CONFIG_FORMS`. La página
 * orquesta load → edit → PATCH → invalidate cache. Tabs "Editar" /
 * "Historial" comparten layout estándar del BO.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchModuleConfig,
  fetchModuleConfigAudit,
  patchModuleConfig,
  type ConfigSlug,
  type ModuleConfigAuditEntry,
  type ModuleConfigResponse,
} from '../../api/admin.js';
import { AdminApiError } from '../../api/admin/_core.js';
import { ModelOverridesSection } from './ModelOverridesSection.js';
import { AnalyzeOverrideSection, type AnalyzeOverrideValue } from './AnalyzeOverrideSection.js';
import { fetchAnalyzeConfig, type AnalyzeConfig } from '../../api/admin/analyze-config.js';
import { fetchAdminPersonas } from '../../api/admin/personas.js';
import type { ModelOverride, TaskType } from '../../api/admin/routing.js';
import { SPEECH_EVENTS, type SpeechEvent, type SpeechMarkupPolicy } from '@orborix/shared-types';

const SLUGS: ConfigSlug[] = ['videos', 'coach', 'languages', 'scout', 'spar'];

interface ConfigFormProps {
  config: Record<string, unknown>;
  /**
   * Body crudo en DB (`null` = fila nunca tocada). El PATCH reemplaza la
   * config ENTERA: un form que sólo edita algunas claves parte de acá para no
   * borrar las que no modela (sin congelar los defaults del schema).
   */
  stored?: Record<string, unknown> | null;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean | undefined;
}

const CONFIG_FORMS: Record<ConfigSlug, (props: ConfigFormProps) => ReactElement> = {
  videos: VideosConfigForm,
  coach: CoachConfigForm,
  languages: LanguagesConfigForm,
  scout: ScoutConfigForm,
  spar: SparConfigForm,
};

const MODULE_DESCRIPTIONS: Record<ConfigSlug, string> = {
  videos: 'Companion de YouTube · Gemini 2.5 Flash · distiller post-sesión',
  coach: 'Acompañamiento emocional · cap diario de gasto',
  languages: 'Coach de idiomas · dual-LLM toggle',
  scout: 'Entrevistas de trabajo por voz · voz + modelo de la entrevistadora',
  spar: 'Oratoria y conversaciones difíciles por voz · voz + modelo del coach',
};

export function ModuleConfigPage(): ReactElement {
  const { slug } = useParams<{ slug: ConfigSlug }>();
  const [tab, setTab] = useState<'edit' | 'audit'>('edit');
  const isValid = slug && (SLUGS as string[]).includes(slug);

  if (!isValid) {
    return (
      <>
        <div className="page-header">
          <div>
            <h1 className="page-title">Module config</h1>
            <p className="page-sub">Slug inválido o no registrado.</p>
          </div>
        </div>
        <section className="card config-empty">
          <p>
            Los módulos con config editable son: <code>videos</code>, <code>coach</code>,{' '}
            <code>languages</code>, <code>scout</code>, <code>spar</code>. Elegí uno arriba para empezar.
          </p>
        </section>
      </>
    );
  }

  const activeSlug = slug;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Module config · {activeSlug}</h1>
          <p className="page-sub">{MODULE_DESCRIPTIONS[activeSlug]}</p>
        </div>
      </div>
      <div className="config-page">
        <nav className="module-nav config-slug-nav" aria-label="Módulos con config">
          {SLUGS.map((s) => (
            <NavLink
              key={s}
              to={`/modules/${s}/config`}
              className={({ isActive }) => (isActive ? 'is-active' : '')}
            >
              {s}
            </NavLink>
          ))}
        </nav>
        <section className="card">
          <header className="card-head config-tabs">
            <div className="segmented" role="tablist" aria-label="Vista">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'edit'}
                className={tab === 'edit' ? 'is-active' : ''}
                onClick={() => setTab('edit')}
              >
                Editar
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'audit'}
                className={tab === 'audit' ? 'is-active' : ''}
                onClick={() => setTab('audit')}
              >
                Historial
              </button>
            </div>
            <small>
              Los cambios surten efecto en la <strong>próxima sesión</strong> · no afectan
              sesiones en vuelo
            </small>
          </header>
          {tab === 'edit' ? (
            <ConfigEditor slug={activeSlug} />
          ) : (
            <ConfigAuditView slug={activeSlug} />
          )}
        </section>
      </div>
    </>
  );
}

function ConfigEditor({ slug }: { slug: ConfigSlug }): ReactElement {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'module-config', slug],
    queryFn: () => fetchModuleConfig(slug),
  });
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const effective = useMemo(
    () => draft ?? query.data?.config ?? null,
    [draft, query.data],
  );

  const mutation = useMutation({
    mutationFn: (next: Record<string, unknown>) => patchModuleConfig(slug, next),
    onSuccess: (data: ModuleConfigResponse) => {
      setDraft(null);
      setToast({ kind: 'ok', text: 'Configuración guardada' });
      qc.setQueryData(['admin', 'module-config', slug], data);
      void qc.invalidateQueries({ queryKey: ['admin', 'module-config-audit', slug] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof AdminApiError ? err.message : String(err);
      setToast({ kind: 'err', text: msg });
    },
  });

  if (query.isPending) return <div className="config-state">Cargando…</div>;
  if (query.isError)
    return <div className="config-state config-state-err">Error: {(query.error).message}</div>;
  if (!effective) return <div className="config-state">Sin datos</div>;

  const FormComponent = CONFIG_FORMS[slug];
  const isDirty = draft !== null;

  return (
    <div className="config-editor">
      <div className="config-meta">
        <span className="config-meta-label">Última edición</span>
        <span className="config-meta-value mono">
          {query.data?.updatedAt
            ? new Date(query.data.updatedAt).toLocaleString('es-AR', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })
            : 'nunca · usando defaults del schema'}
        </span>
      </div>
      <FormComponent
        config={effective}
        stored={query.data?.storedConfig ?? null}
        onChange={(next) => setDraft(next)}
        disabled={mutation.isPending}
      />
      <footer className="config-footer">
        <div className="config-footer-left">
          {isDirty && (
            <span className="config-dirty-pill">
              <span className="config-dirty-dot" aria-hidden="true" />
              Hay cambios sin guardar
            </span>
          )}
          {toast && (
            <span
              className={toast.kind === 'ok' ? 'config-toast ok' : 'config-toast err'}
              role="status"
            >
              {toast.text}
            </span>
          )}
        </div>
        <div className="config-footer-actions">
          {isDirty && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => setDraft(null)}
              disabled={mutation.isPending}
            >
              Descartar
            </button>
          )}
          <button
            type="button"
            className="btn primary"
            disabled={!isDirty || mutation.isPending}
            onClick={() => draft && mutation.mutate(draft)}
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </footer>
    </div>
  );
}

function ConfigAuditView({ slug }: { slug: ConfigSlug }): ReactElement {
  const query = useQuery({
    queryKey: ['admin', 'module-config-audit', slug],
    queryFn: () => fetchModuleConfigAudit(slug),
  });
  if (query.isPending) return <div className="config-state">Cargando…</div>;
  if (query.isError)
    return <div className="config-state config-state-err">Error: {(query.error).message}</div>;
  const entries = query.data?.entries ?? [];
  if (entries.length === 0) {
    return (
      <div className="config-state">
        Sin cambios registrados todavía. El primer PATCH va a aparecer acá.
      </div>
    );
  }
  return (
    <div className="config-audit">
      {entries.map((entry) => (
        <AuditEntryCard key={entry.id} entry={entry} />
      ))}
    </div>
  );
}

function AuditEntryCard({ entry }: { entry: ModuleConfigAuditEntry }): ReactElement {
  const when = new Date(entry.changedAt).toLocaleString('es-AR', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
  return (
    <article className="audit-entry">
      <header className="audit-entry-head">
        <span className="audit-entry-when">{when}</span>
        <span className="audit-entry-by mono">
          por <code>{entry.changedByUserId?.slice(0, 8) ?? '—'}</code>
        </span>
      </header>
      <div className="audit-entry-diff">
        <div className="audit-side">
          <span className="audit-side-label">Antes</span>
          <pre className="audit-json">
            {entry.beforeConfig ? JSON.stringify(entry.beforeConfig, null, 2) : '— (sin row previa)'}
          </pre>
        </div>
        <div className="audit-side">
          <span className="audit-side-label">Después</span>
          <pre className="audit-json">{JSON.stringify(entry.afterConfig, null, 2)}</pre>
        </div>
      </div>
    </article>
  );
}

// ─── Forms per slug ──────────────────────────────────────────────────────

interface VideosConfigShape {
  distiller: { enabled: boolean; mode: 'llm' | 'auto-summary' };
  gemini: {
    mediaResolution: 'LOW' | 'MEDIUM' | 'HIGH';
    maxDurationSec: number;
    thinkingBudget: number;
    maxOutputTokens: number;
  };
  modelOverrides: Partial<Record<TaskType, ModelOverride>>;
  analyze?: AnalyzeOverrideValue;
}

function useGlobalAnalyzeSnapshot(): AnalyzeConfig | null {
  const q = useQuery({
    queryKey: ['admin', 'analyze-config'],
    queryFn: fetchAnalyzeConfig,
  });
  return q.data?.config ?? null;
}

const VIDEOS_OVERRIDE_TASKS: readonly TaskType[] = [
  'llm.main',
  'llm.translate',
  'llm.summarize',
  'video.analyze',
];

function VideosConfigForm({ config, onChange, disabled }: ConfigFormProps): ReactElement {
  const cfg = config as unknown as VideosConfigShape;
  const globalAnalyze = useGlobalAnalyzeSnapshot();
  function update(patch: {
    distiller?: Partial<VideosConfigShape['distiller']>;
    gemini?: Partial<VideosConfigShape['gemini']>;
    modelOverrides?: VideosConfigShape['modelOverrides'];
    analyze?: AnalyzeOverrideValue | undefined;
  }): void {
    const next: Record<string, unknown> = {
      distiller: { ...cfg.distiller, ...(patch.distiller ?? {}) },
      gemini: { ...cfg.gemini, ...(patch.gemini ?? {}) },
      modelOverrides: patch.modelOverrides ?? cfg.modelOverrides,
    };
    const nextAnalyze = 'analyze' in patch ? patch.analyze : cfg.analyze;
    if (nextAnalyze !== undefined) next['analyze'] = nextAnalyze;
    onChange(next);
  }
  return (
    <div className="config-form">
      <section className="config-section">
        <header className="config-section-head">
          <h4 className="config-section-title">Distiller</h4>
          <p className="config-section-sub">
            Job post-sesión que extrae highlights al memory layer
          </p>
        </header>
        <div className="config-fields">
          <ToggleField
            label="Habilitar distiller"
            hint={
              <>
                Si está apagado, el job <code>session.distill</code> no se encola al cerrar
                sesiones de <code>videos</code>
              </>
            }
            checked={cfg.distiller.enabled}
            onChange={(enabled) => update({ distiller: { ...cfg.distiller, enabled } })}
            disabled={disabled}
          />
          <SelectField
            label="Modo"
            hint="LLM = pasada Anthropic adicional · auto-summary usa el resumen ya extraído por Gemini"
            value={cfg.distiller.mode}
            options={[
              { value: 'auto-summary', label: 'auto-summary (sin LLM extra)' },
              { value: 'llm', label: 'llm (Anthropic distiller)' },
            ]}
            onChange={(mode) =>
              update({
                distiller: { ...cfg.distiller, mode: mode as 'llm' | 'auto-summary' },
              })
            }
            disabled={disabled}
          />
        </div>
      </section>

      <section className="config-section">
        <header className="config-section-head">
          <h4 className="config-section-title">Gemini</h4>
          <p className="config-section-sub">
            Parámetros del LLM principal y del load inicial
          </p>
        </header>
        <div className="config-fields">
          <SelectField
            label="Media resolution"
            hint="LOW ≈ 64 tokens/frame · HIGH ≈ 256 tokens/frame — impacta costo del video"
            value={cfg.gemini.mediaResolution}
            options={[
              { value: 'LOW', label: 'LOW' },
              { value: 'MEDIUM', label: 'MEDIUM' },
              { value: 'HIGH', label: 'HIGH' },
            ]}
            onChange={(v) =>
              update({
                gemini: { ...cfg.gemini, mediaResolution: v as 'LOW' | 'MEDIUM' | 'HIGH' },
              })
            }
            disabled={disabled}
          />
          <NumberField
            label="Max duration"
            hint={`${formatMin(cfg.gemini.maxDurationSec)} — videos más largos se rechazan al load`}
            value={cfg.gemini.maxDurationSec}
            min={60}
            max={14400}
            step={60}
            suffix="s"
            presets={[
              { label: '30m', value: 1800 },
              { label: '1h', value: 3600 },
              { label: '2h', value: 7200 },
              { label: '4h', value: 14400 },
            ]}
            onChange={(maxDurationSec) =>
              update({ gemini: { ...cfg.gemini, maxDurationSec } })
            }
            disabled={disabled}
          />
          <NumberField
            label="Thinking budget"
            hint="0 = sin thinking (recomendado para chat conversacional)"
            value={cfg.gemini.thinkingBudget}
            min={0}
            max={1024}
            step={64}
            suffix="tokens"
            onChange={(thinkingBudget) =>
              update({ gemini: { ...cfg.gemini, thinkingBudget } })
            }
            disabled={disabled}
          />
          <NumberField
            label="Max output tokens"
            hint="Cap por respuesta del LLM principal"
            value={cfg.gemini.maxOutputTokens}
            min={256}
            max={8192}
            step={256}
            suffix="tokens"
            onChange={(maxOutputTokens) =>
              update({ gemini: { ...cfg.gemini, maxOutputTokens } })
            }
            disabled={disabled}
          />
        </div>
      </section>

      <ModelOverridesSection
        tasks={VIDEOS_OVERRIDE_TASKS}
        value={cfg.modelOverrides ?? {}}
        onChange={(modelOverrides) => update({ modelOverrides })}
        disabled={disabled}
      />

      <AnalyzeOverrideSection
        value={cfg.analyze}
        onChange={(analyze) => update({ analyze })}
        disabled={disabled}
        globalSnapshot={globalAnalyze}
      />
    </div>
  );
}

interface ScoutConfigShape {
  modelOverrides: Partial<Record<TaskType, ModelOverride>>;
  /** Slug de la persona (catálogo central) cuya voz usa la entrevistadora. */
  voicePersonaSlug?: string;
}

// Scout sólo conduce la charla con `llm.main`; no usa otras tasks LLM.
const SCOUT_OVERRIDE_TASKS: readonly TaskType[] = ['llm.main'];

function ScoutConfigForm({ config, onChange, disabled }: ConfigFormProps): ReactElement {
  const cfg = config as unknown as ScoutConfigShape;
  const personasQuery = useQuery({
    queryKey: ['admin', 'personas'],
    queryFn: fetchAdminPersonas,
  });
  const personas = personasQuery.data ?? [];

  function emit(patch: {
    modelOverrides?: Partial<Record<TaskType, ModelOverride>>;
    voicePersonaSlug?: string | undefined;
  }): void {
    const next: Record<string, unknown> = {
      modelOverrides: patch.modelOverrides ?? cfg.modelOverrides ?? {},
    };
    const slug = 'voicePersonaSlug' in patch ? patch.voicePersonaSlug : cfg.voicePersonaSlug;
    // Omitir la clave cuando está vacío (schema la trata como opcional → cae
    // a la voz del `.md`). No mandar string vacío (el schema pide min(1)).
    if (slug) next['voicePersonaSlug'] = slug;
    onChange(next);
  }

  return (
    <div className="config-form">
      <section className="config-section">
        <header className="config-section-head">
          <h4 className="config-section-title">Voz de la entrevistadora</h4>
          <p className="config-section-sub">
            Voz del catálogo central de personas. Vacío = usa la voz definida en la
            guía del rol (<code>.md</code>).
          </p>
        </header>
        <div className="config-fields">
          <SelectField
            label="Voz"
            hint={
              personasQuery.isError
                ? 'No se pudo cargar el catálogo de voces'
                : 'Persona/voz con la que habla la entrevistadora'
            }
            value={cfg.voicePersonaSlug ?? ''}
            options={[
              { value: '', label: '— Usar voz del rol (.md) —' },
              ...personas.map((p) => ({
                value: p.slug,
                label: `${p.name}${p.accent ? ` · ${p.accent}` : ''}${
                  p.category ? ` (${p.category})` : ''
                }`,
              })),
            ]}
            onChange={(slug) => emit({ voicePersonaSlug: slug || undefined })}
            disabled={disabled || personasQuery.isPending}
          />
        </div>
      </section>

      <ModelOverridesSection
        tasks={SCOUT_OVERRIDE_TASKS}
        value={cfg.modelOverrides ?? {}}
        onChange={(modelOverrides) => emit({ modelOverrides })}
        disabled={disabled}
      />
    </div>
  );
}

interface SparConfigShape {
  modelOverrides: Partial<Record<TaskType, ModelOverride>>;
  /** Slug de la persona (catálogo central) cuya voz usa el coach de Spar. */
  voicePersonaSlug?: string;
}

// Spar sólo conduce la sesión con `llm.main`; no usa otras tasks LLM.
const SPAR_OVERRIDE_TASKS: readonly TaskType[] = ['llm.main'];

function SparConfigForm({ config, onChange, disabled }: ConfigFormProps): ReactElement {
  const cfg = config as unknown as SparConfigShape;
  const personasQuery = useQuery({
    queryKey: ['admin', 'personas'],
    queryFn: fetchAdminPersonas,
  });
  const personas = personasQuery.data ?? [];

  function emit(patch: {
    modelOverrides?: Partial<Record<TaskType, ModelOverride>>;
    voicePersonaSlug?: string | undefined;
  }): void {
    const next: Record<string, unknown> = {
      modelOverrides: patch.modelOverrides ?? cfg.modelOverrides ?? {},
    };
    const slug = 'voicePersonaSlug' in patch ? patch.voicePersonaSlug : cfg.voicePersonaSlug;
    // Omitir la clave cuando está vacío (schema opcional → cae a la persona
    // default del sistema). No mandar string vacío (el schema pide min(1)).
    if (slug) next['voicePersonaSlug'] = slug;
    onChange(next);
  }

  return (
    <div className="config-form">
      <section className="config-section">
        <header className="config-section-head">
          <h4 className="config-section-title">Voz del coach</h4>
          <p className="config-section-sub">
            Voz del catálogo central de personas. Vacío = usa la persona default del
            sistema.
          </p>
        </header>
        <div className="config-fields">
          <SelectField
            label="Voz"
            hint={
              personasQuery.isError
                ? 'No se pudo cargar el catálogo de voces'
                : 'Persona/voz con la que habla el coach y la audiencia'
            }
            value={cfg.voicePersonaSlug ?? ''}
            options={[
              { value: '', label: '— Usar persona default del sistema —' },
              ...personas.map((p) => ({
                value: p.slug,
                label: `${p.name}${p.accent ? ` · ${p.accent}` : ''}${
                  p.category ? ` (${p.category})` : ''
                }`,
              })),
            ]}
            onChange={(slug) => emit({ voicePersonaSlug: slug || undefined })}
            disabled={disabled || personasQuery.isPending}
          />
        </div>
      </section>

      <ModelOverridesSection
        tasks={SPAR_OVERRIDE_TASKS}
        value={cfg.modelOverrides ?? {}}
        onChange={(modelOverrides) => emit({ modelOverrides })}
        disabled={disabled}
      />
    </div>
  );
}

interface CoachConfigShape {
  dailyCapNano: number;
  conversationMode?: 'classic' | 'realtime';
  modelOverrides: Partial<Record<TaskType, ModelOverride>>;
  analyze?: AnalyzeOverrideValue;
}

// `voice.realtime` = tier del modelo S2S del modo realtime (full vs mini).
const COACH_OVERRIDE_TASKS: readonly TaskType[] = [
  'llm.main',
  'llm.distill',
  'llm.reflect',
  'llm.safety',
  'voice.realtime',
];

// Convención canónica del repo (ver apps/admin/src/lib/format.ts NANO_PER_USD).
// 1 USD = 100_000_000 nano (10^8). 1 nano = $0.00000001.
const NANO_PER_USD = 100_000_000;

function CoachConfigForm({ config, onChange, disabled }: ConfigFormProps): ReactElement {
  const cfg = config as unknown as CoachConfigShape;
  const globalAnalyze = useGlobalAnalyzeSnapshot();
  const usd = cfg.dailyCapNano / NANO_PER_USD;
  function emit(next: {
    dailyCapNano?: number;
    conversationMode?: 'classic' | 'realtime';
    modelOverrides?: Partial<Record<TaskType, ModelOverride>>;
    analyze?: AnalyzeOverrideValue | undefined;
  }): void {
    const merged: Record<string, unknown> = {
      dailyCapNano: next.dailyCapNano ?? cfg.dailyCapNano,
      conversationMode: next.conversationMode ?? cfg.conversationMode ?? 'classic',
      modelOverrides: next.modelOverrides ?? cfg.modelOverrides ?? {},
    };
    const nextAnalyze = 'analyze' in next ? next.analyze : cfg.analyze;
    if (nextAnalyze !== undefined) merged['analyze'] = nextAnalyze;
    onChange(merged);
  }
  return (
    <div className="config-form">
      <section className="config-section">
        <header className="config-section-head">
          <h4 className="config-section-title">Modo de conversación</h4>
          <p className="config-section-sub">
            Pipeline de voz de las sesiones del coach. Surte efecto en la PRÓXIMA
            sesión (no muta charlas en vuelo).
          </p>
        </header>
        <div className="config-fields">
          <SelectField
            label="Modo"
            hint={
              (cfg.conversationMode ?? 'classic') === 'realtime'
                ? '⚠️ Modo de DEV/uso propio: el safety de crisis NO corre en esta rama — no habilitar para users reales (plan realtime, F5 descartada)'
                : 'classic = STT→LLM→TTS (ElevenLabs) · realtime = speech-to-speech full-duplex (OpenAI gpt-realtime, voz nativa)'
            }
            value={cfg.conversationMode ?? 'classic'}
            options={[
              { value: 'classic', label: 'classic — STT→LLM→TTS (default)' },
              { value: 'realtime', label: 'realtime — S2S full-duplex (sólo dev)' },
            ]}
            onChange={(mode) =>
              emit({ conversationMode: mode === 'realtime' ? 'realtime' : 'classic' })
            }
            disabled={disabled}
          />
        </div>
      </section>

      <section className="config-section">
        <header className="config-section-head">
          <h4 className="config-section-title">Cap diario</h4>
          <p className="config-section-sub">
            Tope de gasto por user/día · expresado en nano-USD (1 USD = 10⁸ nano)
          </p>
        </header>
        <div className="config-fields">
          <NumberField
            label="Daily cap"
            hint={
              cfg.dailyCapNano === 0
                ? 'Cap deshabilitado — el módulo no rechaza sesiones por gasto'
                : `≈ USD ${usd.toFixed(2)} por user/día`
            }
            value={cfg.dailyCapNano}
            min={0}
            max={Number.MAX_SAFE_INTEGER}
            step={NANO_PER_USD / 100}
            suffix="nano"
            presets={[
              { label: '$0 (off)', value: 0 },
              { label: '$0.25', value: 25 * (NANO_PER_USD / 100) },
              { label: '$0.50', value: 50 * (NANO_PER_USD / 100) },
              { label: '$1.00', value: 1 * NANO_PER_USD },
              { label: '$5.00', value: 5 * NANO_PER_USD },
              { label: '$500', value: 500 * NANO_PER_USD },
            ]}
            onChange={(dailyCapNano) => emit({ dailyCapNano })}
            disabled={disabled}
          />
        </div>
      </section>

      <ModelOverridesSection
        tasks={COACH_OVERRIDE_TASKS}
        value={cfg.modelOverrides ?? {}}
        onChange={(modelOverrides) => emit({ modelOverrides })}
        disabled={disabled}
      />

      <AnalyzeOverrideSection
        value={cfg.analyze}
        onChange={(analyze) => emit({ analyze })}
        disabled={disabled}
        globalSnapshot={globalAnalyze}
      />
    </div>
  );
}

interface LanguagesConfigShape {
  modelOverrides?: Partial<Record<TaskType, ModelOverride>>;
  analyze?: AnalyzeOverrideValue;
  speechMarkup?: SpeechMarkupPolicy;
  /** Free-talk skill credit (W5): mounts the judge at session start, kill switch per turn. */
  skillCreditEnabled?: boolean;
  /** The ≥ 95 % variant (a pair check on top of the turn check), read per turn. */
  skillCreditPairCheck?: boolean;
  /** Accounts that get a published OTA bundle at once, whatever its rollout (plan 49 § 3.3). */
  otaEarlyAccessEmails?: string[];
}

// `llm.main` = reply conversacional (texto plano por streaming, sin tools).
// `llm.corrections` = side-runner que extrae correcciones en paralelo.
const LANGUAGES_OVERRIDE_TASKS: readonly TaskType[] = [
  'llm.main',
  'llm.corrections',
];

function LanguagesConfigForm({ config, stored, onChange, disabled }: ConfigFormProps): ReactElement {
  const cfg = config as unknown as LanguagesConfigShape;
  const globalAnalyze = useGlobalAnalyzeSnapshot();
  function emit(next: {
    modelOverrides?: Partial<Record<TaskType, ModelOverride>>;
    analyze?: AnalyzeOverrideValue | undefined;
    speechMarkup?: SpeechMarkupPolicy;
    skillCreditEnabled?: boolean;
    skillCreditPairCheck?: boolean;
    otaEarlyAccessEmails?: string[];
  }): void {
    // Parte del body guardado (+ lo ya editado en el draft): este form no modela
    // todas las claves (geminiTtsVoice, toggles…) y el PATCH reemplaza entero.
    const merged: Record<string, unknown> = {
      ...(stored ?? {}),
      modelOverrides: next.modelOverrides ?? cfg.modelOverrides ?? {},
    };
    const nextAnalyze = 'analyze' in next ? next.analyze : cfg.analyze;
    if (nextAnalyze !== undefined) merged['analyze'] = nextAnalyze;
    else delete merged['analyze'];
    const nextMarkup = next.speechMarkup ?? cfg.speechMarkup;
    if (nextMarkup !== undefined) merged['speechMarkup'] = nextMarkup;
    if (next.skillCreditEnabled !== undefined) merged['skillCreditEnabled'] = next.skillCreditEnabled;
    if (next.skillCreditPairCheck !== undefined) merged['skillCreditPairCheck'] = next.skillCreditPairCheck;
    if (next.otaEarlyAccessEmails !== undefined) merged['otaEarlyAccessEmails'] = next.otaEarlyAccessEmails;
    onChange(merged);
  }
  const markup = cfg.speechMarkup;
  return (
    <div className="config-form">
      <ModelOverridesSection
        tasks={LANGUAGES_OVERRIDE_TASKS}
        value={cfg.modelOverrides ?? {}}
        onChange={(modelOverrides) => emit({ modelOverrides })}
        disabled={disabled}
      />

      <AnalyzeOverrideSection
        value={cfg.analyze}
        onChange={(analyze) => emit({ analyze })}
        disabled={disabled}
        globalSnapshot={globalAnalyze}
      />

      {markup && (
        <SpeechMarkupSection
          value={markup}
          onChange={(speechMarkup) => emit({ speechMarkup })}
          disabled={disabled}
        />
      )}
      <section className="config-section">
        <header className="config-section-head">
          <h4 className="config-section-title">Crédito de habilidades (charla libre)</h4>
          <p className="config-section-sub">
            Jev propone y Gemini confirma qué habilidades del catálogo usó el alumno en cada
            turno: chips en vivo, «lo clavaste» y progreso. Necesita la key de TypeSafe del
            backend (sin ella la sesión NO arranca con esto prendido). Se monta al iniciar la
            sesión; apagarlo corta las llamadas desde el turno siguiente.
          </p>
        </header>
        <ToggleField
          label="Prendido"
          checked={cfg.skillCreditEnabled === true}
          onChange={(skillCreditEnabled) => emit({ skillCreditEnabled })}
          disabled={disabled}
        />
        <ToggleField
          label="Variante ≥ 95 % (chequeo por par)"
          hint="Más aciertos (92 → 96 % medido) a cambio de más créditos falsos y ~30 % más costo. Dejar apagado salvo decisión del dueño."
          checked={cfg.skillCreditPairCheck === true}
          onChange={(skillCreditPairCheck) => emit({ skillCreditPairCheck })}
          disabled={disabled}
        />
      </section>
      <section className="config-section">
        <header className="config-section-head">
          <h4 className="config-section-title">Early access OTA (app nativa)</h4>
          <p className="config-section-sub">
            Estas cuentas reciben un bundle OTA publicado apenas sale, sin esperar el
            despliegue gradual (10 → 50 → 100 %). No saltea nada más: un canal pausado, un
            build nativo incompatible o un bundle que falló en el teléfono lo siguen frenando.
            Surte efecto en el próximo arranque de la app o refresco de sesión.
          </p>
        </header>
        <EmailListField
          label="Emails (uno por línea, máximo 100)"
          value={cfg.otaEarlyAccessEmails ?? []}
          onChange={(otaEarlyAccessEmails) => emit({ otaEarlyAccessEmails })}
          disabled={disabled}
        />
      </section>
    </div>
  );
}

/**
 * Dial del markup expresivo de voz (speak sobre Gemini TTS): kill switch +
 * qué sonidos puede usar el coach. Surte efecto en la PRÓXIMA sesión.
 */
function SpeechMarkupSection({
  value,
  onChange,
  disabled,
}: {
  value: SpeechMarkupPolicy;
  onChange: (next: SpeechMarkupPolicy) => void;
  disabled?: boolean | undefined;
}): ReactElement {
  const enabled = new Set<SpeechEvent>(value.events);
  function toggleEvent(e: SpeechEvent, on: boolean): void {
    // Orden canónico del contrato, sin duplicados.
    const events = SPEECH_EVENTS.filter((x) => (x === e ? on : enabled.has(x)));
    onChange({ enabled: value.enabled, events });
  }
  return (
    <section className="config-section">
      <header className="config-section-head">
        <h4 className="config-section-title">Voz expresiva (Gemini)</h4>
        <p className="config-section-sub">
          Sonidos, ánimo y énfasis que la voz actúa. Apagado = sin bloque en el prompt y la voz
          lee el texto tal cual (como ElevenLabs). Si el coach sobreactúa un sonido, destildalo.
          Surte efecto en la PRÓXIMA sesión.
        </p>
      </header>
      <div className="config-fields">
        <ToggleField
          label="Markup expresivo activo"
          checked={value.enabled}
          onChange={(on) => onChange({ enabled: on, events: value.events })}
          disabled={disabled}
        />
        {SPEECH_EVENTS.map((e) => (
          <ToggleField
            key={e}
            label={`<${e}>`}
            checked={enabled.has(e)}
            onChange={(on) => toggleEvent(e, on)}
            disabled={disabled === true || !value.enabled}
          />
        ))}
      </div>
    </section>
  );
}

function formatMin(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

// ─── Atomic field components ─────────────────────────────────────────────

interface ToggleFieldProps {
  label: string;
  hint?: ReactElement | string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean | undefined;
}

function ToggleField({ label, hint, checked, onChange, disabled }: ToggleFieldProps): ReactElement {
  return (
    <div className="config-field">
      <label className="config-field-toggle">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        <span className="config-toggle-track" aria-hidden="true">
          <span className="config-toggle-thumb" />
        </span>
        <span className="config-field-label">{label}</span>
      </label>
      {hint && <p className="config-field-hint">{hint}</p>}
    </div>
  );
}

interface EmailListFieldProps {
  label: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean | undefined;
}

/**
 * One email per line. The text is kept locally so a trailing newline or a
 * half-typed address survives the round trip; the emitted list drops empty
 * lines and is validated by the backend schema on save.
 */
function EmailListField({ label, value, onChange, disabled }: EmailListFieldProps): ReactElement {
  const [text, setText] = useState(() => value.join('\n'));
  return (
    <div className="config-field">
      <label className="config-field-label">{label}</label>
      <textarea
        className="config-input"
        rows={4}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(
            e.target.value
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0),
          );
        }}
        disabled={disabled}
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  hint?: ReactElement | string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (next: string) => void;
  disabled?: boolean | undefined;
}

function SelectField({ label, hint, value, options, onChange, disabled }: SelectFieldProps): ReactElement {
  return (
    <div className="config-field">
      <label className="config-field-label">{label}</label>
      <select
        className="config-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && <p className="config-field-hint">{hint}</p>}
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  hint?: ReactElement | string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  presets?: ReadonlyArray<{ label: string; value: number }>;
  onChange: (next: number) => void;
  disabled?: boolean | undefined;
}

function NumberField({
  label,
  hint,
  value,
  min,
  max,
  step,
  suffix,
  presets,
  onChange,
  disabled,
}: NumberFieldProps): ReactElement {
  return (
    <div className="config-field">
      <label className="config-field-label">{label}</label>
      <div className="config-input-row">
        <input
          type="number"
          className="config-input"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n)) onChange(n);
          }}
          disabled={disabled}
        />
        {suffix && <span className="config-input-suffix">{suffix}</span>}
      </div>
      {presets && presets.length > 0 && (
        <div className="config-presets">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              className={value === p.value ? 'config-preset is-active' : 'config-preset'}
              onClick={() => onChange(p.value)}
              disabled={disabled}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
      {hint && <p className="config-field-hint">{hint}</p>}
    </div>
  );
}
